import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const KIND_OPTIONS = [["video", "Video consulto"], ["domicilio", "A domicilio"], ["visita", "Visita in presenza"]];
const PRICING_OPTIONS = [["fissa", "Fissa"], ["oraria", "Oraria (× durata)"], ["forfettaria", "Forfettaria"]];
const OPTION_LABELS = Object.fromEntries([...KIND_OPTIONS, ...PRICING_OPTIONS]);

export const CATALOG_FIELDS = {
  departments: [["slug", "Slug"], ["title", "Titolo"], ["description", "Descrizione"], ["narration", "Narrazione vocale (giro guidato)"], ["panel_type", "Tipo pannello (telemedicina|domiciliare|richieste|documenti|agenda|ai|info)"], ["icon", "Icona (Video, Home, CalendarDays, FileSignature, FolderLock, Bot, Stethoscope)"], ["x", "Posizione X (-16..16)", "number"], ["z", "Posizione Z (-22..6)", "number"], ["rotation", "Rotazione (rad)", "number"], ["floor", "Piano", "number"], ["order", "Ordine", "number"], ["active", "Attivo", "bool"]],
  services: [["name", "Nome"], ["kind", "Tipo di prestazione", "select", KIND_OPTIONS], ["pricing_type", "Tipo tariffa", "select", PRICING_OPTIONS], ["price", "Tariffa € (oraria: €/ora)", "number"], ["department_slug", "Reparto (slug)"], ["specialty", "Specializzazione"], ["description", "Descrizione"], ["duration_min", "Durata min", "number"], ["active", "Attivo", "bool"]],
  professionals: [["title", "Titolo"], ["name", "Nome"], ["profession", "Professione"], ["specialty", "Specializzazione"], ["bio", "Bio"], ["kinds", "Servizi (video,domicilio,visita)", "list"], ["cities", "Località", "list"], ["user_id", "User ID collegato"], ["schedule.days", "Giorni (0=lun…6=dom)", "numlist"], ["schedule.start", "Inizio (HH:MM)"], ["schedule.end", "Fine (HH:MM)"], ["schedule.slot_minutes", "Slot min", "number"], ["active", "Attivo", "bool"]],
  content: [["title", "Titolo"], ["body", "Testo"], ["placement", "Posizione"], ["active", "Attivo", "bool"]],
};

const get = (o, k) => k.split(".").reduce((a, p) => a?.[p], o);
const setIn = (o, k, v) => {
  const [h, ...r] = k.split(".");
  return r.length ? { ...o, [h]: setIn(o[h] || {}, r.join("."), v) } : { ...o, [h]: v };
};

const parseValue = (raw, type) => {
  if (type === "number") return Number(raw);
  if (type === "list") return raw.split(",").map((s) => s.trim()).filter(Boolean);
  if (type === "numlist") return raw.split(",").map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n));
  return raw;
};

function FieldInput({ k, type, options, value, onChange }) {
  if (type === "bool") return <Switch checked={!!value} onCheckedChange={onChange} data-testid={`catalog-field-${k}`} />;
  if (type === "select") {
    return (
      <Select value={value || options[0][0]} onValueChange={onChange}>
        <SelectTrigger data-testid={`catalog-field-${k}`}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`catalog-option-${k}-${v}`}>{l}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  return <Input data-testid={`catalog-field-${k}`} type={type === "number" ? "number" : "text"} step="any" value={Array.isArray(value) ? value.join(", ") : value ?? ""} onChange={onChange} />;
}

function Editor({ fields, item, onSave, onClose }) {
  const [f, setF] = useState(item);
  const change = (k, type) => (e) => {
    const raw = type === "bool" || type === "select" ? e : e.target.value;
    setF(setIn(f, k, parseValue(raw, type)));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="catalog-editor">
        <DialogHeader><DialogTitle>{item.id ? "Modifica" : "Nuovo elemento"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map(([k, l, type, options]) => (
            <div key={k} className="space-y-1">
              <Label>{l}</Label>
              <FieldInput k={k} type={type} options={options} value={get(f, k)} onChange={change(k, type)} />
            </div>
          ))}
          <Button data-testid="catalog-save-btn" onClick={() => onSave(f)} className="w-full bg-sky-600 hover:bg-sky-700">Salva</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CatalogTable({ collection, onChange }) {
  const fields = CATALOG_FIELDS[collection];
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get(`/admin/catalog/${collection}`).then(({ data }) => setItems(data)), [collection]);
  useEffect(() => {
    load();
  }, [load]);
  const save = async (f) => {
    const { id, created_at, ...body } = f;
    try {
      if (id) await api.put(`/admin/catalog/${collection}/${id}`, body);
      else await api.post(`/admin/catalog/${collection}`, body);
      toast.success("Salvato");
      setEdit(null);
      load();
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const blank = collection === "professionals" ? { kinds: ["video"], cities: [], schedule: { days: [0, 1, 2, 3, 4], start: "09:00", end: "17:00", slot_minutes: 30 }, active: true }
    : collection === "services" ? { kind: "video", pricing_type: "fissa", duration_min: 30, active: true } : { active: true };
  return (
    <div data-testid={`catalog-${collection}`}>
      <Button data-testid={`catalog-add-${collection}`} onClick={() => setEdit(blank)} className="mb-3 rounded-full bg-sky-600 hover:bg-sky-700"><Plus className="mr-1 h-4 w-4" /> Aggiungi</Button>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50 text-slate-500"><tr>{fields.slice(0, 4).map(([k, l]) => <th key={k} className="px-3 py-2 font-semibold">{l.split(" (")[0]}</th>)}<th className="px-3 py-2">Attivo</th><th /></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`catalog-row-${it.id}`}>
                {fields.slice(0, 4).map(([k, , type]) => <td key={k} className="max-w-[220px] truncate px-3 py-2">{type === "select" ? OPTION_LABELS[get(it, k) || "fissa"] || get(it, k) : String(get(it, k) ?? "")}</td>)}
                <td className="px-3 py-2">{it.active ? "sì" : "no"}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <Button size="icon" variant="ghost" data-testid={`catalog-edit-${it.id}`} onClick={() => setEdit(it)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`catalog-delete-${it.id}`} onClick={() => api.delete(`/admin/catalog/${collection}/${it.id}`).then(() => { load(); onChange?.(); })}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && <Editor fields={fields} item={edit} onSave={save} onClose={() => setEdit(null)} />}
    </div>
  );
}
